import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useSyncExternalStore,
} from 'react';

import type { Session, SessionState } from './session';

const SessionContext = createContext<Session | null>(null);

/**
 * Makes `session` available to the app and runs it: starts the opening exchange on mount and stops
 * the proactive renewal on unmount, so no timer outlives the app (S2 R18). Safe under StrictMode:
 * `start` joins an exchange already running.
 */
export function SessionProvider({
  session,
  children,
}: {
  session: Session;
  children: ReactNode;
}): ReactElement {
  useEffect(() => {
    session.start();
    return () => {
      session.stop();
    };
  }, [session]);
  return <SessionContext value={session}>{children}</SessionContext>;
}

function useSessionService(): Session {
  const session = useContext(SessionContext);
  if (session === null) throw new Error('useSession must be used inside <SessionProvider>');
  return session;
}

/**
 * The session as the screens see it: `opening`, `failed` with a `reasonCode`, or `ready` with the
 * `traderId`, `isNew` and `affiliate` of the opening exchange (S2 AC38; S3 reads `isNew` and
 * `affiliate`). The access token is not part of it (R14).
 *
 * @throws {Error} outside `<SessionProvider>`: a wiring bug, not a runtime case.
 */
export function useSession(): SessionState {
  const session = useSessionService();
  return useSyncExternalStore(session.subscribe, session.getState);
}

/**
 * Action behind the "Try again" button of the opening screen (spec §4); ignored unless the
 * session failed with a reason a new attempt can fix.
 *
 * @throws {Error} outside `<SessionProvider>`.
 */
export function useSessionRetry(): () => void {
  return useSessionService().retry;
}
